import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="aside-menu"
export default class extends Controller {
  static targets = [
    "menu",
    "overlay",
    "toggle",
    "breadcrumb",
    "backButton",
    "panel",
    "closeButton"
  ]

  connect() {
    this.animationToken = 0
    this.currentAnimations = []
    this.lastFocusedElement = null
    this.activePanelId = this.rootPanelId
    this.handleKeydown = this.handleKeydown.bind(this)
    this.close({ restoreFocus: false, resetPanel: true })
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown)
    this.cancelAnimations()
    document.body.classList.remove("overflow-hidden")
  }

  open(event) {
    if (event) event.preventDefault()

    this.lastFocusedElement = document.activeElement
    this.cancelAnimations()
    this.showDrawer()
    this.setActivePanel(this.rootPanelId)
    this.focusFirstElement()
  }

  close(eventOrOptions = {}) {
    if (eventOrOptions?.preventDefault) eventOrOptions.preventDefault()

    const options = eventOrOptions?.preventDefault ? {} : eventOrOptions
    const restoreFocus = options.restoreFocus !== false
    const resetPanel = options.resetPanel !== false

    this.cancelAnimations()
    this.hideDrawer()

    if (resetPanel) {
      this.setActivePanel(this.rootPanelId)
    }

    if (restoreFocus && this.lastFocusedElement?.focus) {
      this.lastFocusedElement.focus()
    }
  }

  openPanel(event) {
    event.preventDefault()

    const targetPanelId = event.currentTarget.dataset.mobileMenuOpenPanel
    if (!targetPanelId || targetPanelId === this.activePanelId) return

    this.transitionToPanel(targetPanelId)
  }

  goBack(event) {
    event.preventDefault()

    const currentPanel = this.currentPanel
    if (!currentPanel) return

    const parentPanelId = currentPanel.dataset.mobileMenuParentPanel
    if (!parentPanelId) return

    this.transitionToPanel(parentPanelId, { direction: "backward" })
  }

  goToPanel(event) {
    event.preventDefault()

    const targetPanelId = event.currentTarget.dataset.mobileMenuPanelId
    if (!targetPanelId || targetPanelId === this.activePanelId) return

    const currentDepth = this.panelDepth(this.currentPanel)
    const targetDepth = this.panelDepth(this.findPanel(targetPanelId))
    const direction = targetDepth < currentDepth ? "backward" : "forward"

    this.transitionToPanel(targetPanelId, { direction })
  }

  handleKeydown(event) {
    if (!this.isOpen) return

    if (event.key === "Escape") {
      this.close()
      return
    }

    if (event.key === "Tab") {
      this.trapFocus(event)
    }
  }

  transitionToPanel(targetPanelId, { direction } = {}) {
    const currentPanel = this.currentPanel
    const nextPanel = this.findPanel(targetPanelId)
    if (!nextPanel) return

    const resolvedDirection = direction || this.resolveDirection(currentPanel, nextPanel)
    this.cancelAnimations()

    if (this.prefersReducedMotion || !currentPanel || currentPanel === nextPanel) {
      this.setActivePanel(targetPanelId)
      this.focusFirstElement()
      return
    }

    this.animationToken += 1
    const token = this.animationToken

    this.preparePanelForEntry(nextPanel)

    const outgoingFrames = resolvedDirection === "forward" ? [
      { opacity: 1, transform: "translateX(0)" },
      { opacity: 0, transform: "translateX(-16px)" }
    ] : [
      { opacity: 1, transform: "translateX(0)" },
      { opacity: 0, transform: "translateX(16px)" }
    ]

    const incomingFrames = resolvedDirection === "forward" ? [
      { opacity: 0, transform: "translateX(16px)" },
      { opacity: 1, transform: "translateX(0)" }
    ] : [
      { opacity: 0, transform: "translateX(-16px)" },
      { opacity: 1, transform: "translateX(0)" }
    ]

    const options = { duration: 240, easing: "ease-out", fill: "forwards" }
    const outgoingAnimation = currentPanel.animate(outgoingFrames, options)
    const incomingAnimation = nextPanel.animate(incomingFrames, options)
    this.currentAnimations = [ outgoingAnimation, incomingAnimation ]

    Promise.allSettled([ outgoingAnimation.finished, incomingAnimation.finished ]).then(() => {
      if (token !== this.animationToken) return

      // `fill: forwards` keeps the finished animation's opacity effect alive until cancelled.
      // Remove it before returning to class-based visibility control.
      outgoingAnimation.cancel()
      incomingAnimation.cancel()

      currentPanel.classList.add("hidden")
      currentPanel.style.removeProperty("opacity")
      currentPanel.style.removeProperty("transform")

      nextPanel.style.removeProperty("opacity")
      nextPanel.style.removeProperty("transform")

      this.activePanelId = targetPanelId
      this.syncPanels()
      this.updateBreadcrumb()
      this.updateBackButton()
      this.focusFirstElement()
      this.currentAnimations = []
    })
  }

  setActivePanel(panelId) {
    this.activePanelId = panelId
    this.syncPanels()
    this.updateBreadcrumb()
    this.updateBackButton()
  }

  syncPanels() {
    this.panelTargets.forEach((panel) => {
      const isActive = panel.dataset.panelId === this.activePanelId
      panel.classList.toggle("hidden", !isActive)
      panel.setAttribute("aria-hidden", isActive ? "false" : "true")
    })
  }

  updateBreadcrumb() {
    if (!this.hasBreadcrumbTarget) return

    const trail = this.breadcrumbTrail()
    const content = trail.map((panel, index) => {
      const isCurrent = index === trail.length - 1
      const title = panel.dataset.mobileMenuTitle || "Menu"

      if (isCurrent) {
        return `<span class="text-sm font-medium text-cinder">${title}</span>`
      }

      return `<button type="button" class="text-sm text-cinder/70 transition hover:text-cinder" data-action="click->aside-menu#goToPanel" data-mobile-menu-panel-id="${panel.dataset.panelId}">${title}</button>`
    }).join(`<span class="text-cinder/40" aria-hidden="true">/</span>`)

    this.breadcrumbTarget.innerHTML = content
  }

  updateBackButton() {
    if (!this.hasBackButtonTarget) return

    const showBack = this.activePanelId !== this.rootPanelId
    this.backButtonTarget.classList.toggle("hidden", !showBack)
    this.backButtonTarget.setAttribute("aria-hidden", showBack ? "false" : "true")
  }

  showDrawer() {
    this.menuTarget.classList.remove("-translate-x-full")
    this.overlayTarget.classList.remove("hidden")
    this.overlayTarget.setAttribute("aria-hidden", "false")
    this.menuTarget.setAttribute("aria-hidden", "false")
    this.menuTarget.setAttribute("aria-modal", "true")
    if (this.hasToggleTarget) this.toggleTarget.setAttribute("aria-expanded", "true")

    document.body.classList.add("overflow-hidden")
    document.addEventListener("keydown", this.handleKeydown)
  }

  hideDrawer() {
    this.menuTarget.classList.add("-translate-x-full")
    this.overlayTarget.classList.add("hidden")
    this.overlayTarget.setAttribute("aria-hidden", "true")
    this.menuTarget.setAttribute("aria-hidden", "true")
    this.menuTarget.setAttribute("aria-modal", "false")
    if (this.hasToggleTarget) this.toggleTarget.setAttribute("aria-expanded", "false")

    document.body.classList.remove("overflow-hidden")
    document.removeEventListener("keydown", this.handleKeydown)
  }

  preparePanelForEntry(panel) {
    panel.classList.remove("hidden")
    panel.setAttribute("aria-hidden", "false")
    panel.style.opacity = "0"
    panel.style.transform = "translateX(0)"
  }

  focusFirstElement() {
    const focusTarget = this.currentPanel?.querySelector("button:not([disabled]), a[href], [tabindex]:not([tabindex='-1'])")
      || this.closeButtonTarget
      || this.backButtonTarget
    focusTarget?.focus()
  }

  trapFocus(event) {
    const focusable = this.focusableElements
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
      return
    }

    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  breadcrumbTrail() {
    const trail = []
    let panel = this.currentPanel

    while (panel) {
      trail.unshift(panel)
      const parentPanelId = panel.dataset.mobileMenuParentPanel
      panel = parentPanelId ? this.findPanel(parentPanelId) : null
    }

    return trail
  }

  resolveDirection(currentPanel, nextPanel) {
    return this.panelDepth(nextPanel) > this.panelDepth(currentPanel) ? "forward" : "backward"
  }

  panelDepth(panel) {
    return Number.parseInt(panel?.dataset.mobileMenuDepth || "0", 10)
  }

  findPanel(panelId) {
    return this.panelTargets.find((panel) => panel.dataset.panelId === panelId)
  }

  cancelAnimations() {
    // Prevent completion handlers from cancelled transitions changing panel state later.
    this.animationToken += 1
    this.currentAnimations.forEach((animation) => animation.cancel())
    this.currentAnimations = []
    this.resetPanelStyles()
    this.syncPanels()
  }

  resetPanelStyles() {
    this.panelTargets.forEach((panel) => {
      panel.style.removeProperty("opacity")
      panel.style.removeProperty("transform")
    })
  }

  get currentPanel() {
    return this.findPanel(this.activePanelId)
  }

  get isOpen() {
    return !this.menuTarget.classList.contains("-translate-x-full")
  }

  get rootPanelId() {
    return this.menuTarget.dataset.rootPanelId || "mobile-root"
  }

  get focusableElements() {
    return Array.from(this.menuTarget.querySelectorAll("button:not([disabled]), a[href], [tabindex]:not([tabindex='-1'])"))
      .filter((element) => !element.closest(".hidden"))
  }

  get prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }
}
